/**
 * Vasco GK guard on the client (GK_EXPORT_PLAN.md 5.3): when the server says
 * a change touches a payment that is already exported (409 GK_EXPORTED), ask
 * the user and repeat the same request with confirmGkChange. One axios
 * response interceptor covers every accounting action.
 */

type Confirm = (message: string) => boolean;

interface RequestLike {
  data?: unknown;
  params?: Record<string, unknown>;
  _gkConfirmed?: boolean;
  [key: string]: unknown;
}

/** The request again, with confirmGkChange in the query and in a JSON body. */
export function withGkConfirmation(config: RequestLike): RequestLike {
  let data = config.data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) data = JSON.stringify({ ...parsed, confirmGkChange: true });
    } catch {
      /* not JSON: the query parameter is enough */
    }
  } else if (data && typeof data === 'object' && !(typeof FormData !== 'undefined' && data instanceof FormData)) {
    data = { ...(data as Record<string, unknown>), confirmGkChange: true };
  }
  return { ...config, data, params: { ...(config.params || {}), confirmGkChange: 'true' }, _gkConfirmed: true };
}

/** The error handler of the response interceptor. */
export function gkGuardErrorHandler(client: { request: (config: any) => Promise<unknown> }, confirm: Confirm = (m) => window.confirm(m)) {
  return (error: any) => {
    const body = error?.response?.data;
    const config = error?.config as RequestLike | undefined;
    if (error?.response?.status !== 409 || body?.code !== 'GK_EXPORTED' || !config || config._gkConfirmed) {
      return Promise.reject(error);
    }
    if (!confirm(`${body.error}\n\nDo you want to make the change anyway?`)) return Promise.reject(error);
    return client.request(withGkConfirmation(config));
  };
}
